import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Alert, Button, Modal, Space, Spin, Typography } from 'antd'
import { useTranslation } from 'react-i18next'
import { ReloadOutlined } from '@ant-design/icons'
import { QRCodeSVG } from 'qrcode.react'
import {
  createMobileCaptureSession,
  getMobileCaptureSession,
  isLoopbackOrigin,
  resolveSessionPairingUrl,
  revokeMobileCaptureSession,
  type MobileCaptureSessionOut,
  type MobileAudioPresentationType,
  type MobileCaptureSessionStatus,
  type MobileCaptureTargetFlow,
} from '../mobileCaptureApi'

const { Text, Paragraph } = Typography

/**
 * Canonical mobile-upload modal state machine. There is exactly one source
 * of truth for which panel renders — never render the local-only warning
 * and the QR code at the same time, and never default to a warning state
 * before the session-create call resolves (that was the root cause of the
 * "QR overlaid on top of a stale localhost warning" bug on Preview).
 *
 *   INITIALIZING       -> creating/refreshing the session; show a spinner only
 *   READY               -> mobile_upload_url is mobile-reachable; show the QR
 *   LOCAL_UNREACHABLE   -> mobile_upload_url really is 127.0.0.1/localhost and
 *                          no public/LAN URL is configured (Local dev only)
 *   ERROR               -> session create or refresh failed outright
 */
type ModalPhase = 'initializing' | 'ready' | 'local_unreachable' | 'error'

type ErrorCode = 'QR_SESSION_CREATE_FAILED' | 'NETWORK_ERROR'

interface Props {
  open: boolean
  workspaceId: string
  onClose: () => void
  /** Which canonical processing flow a captured photo should enter. Passed
   * once at session-creation time; the backend stores it on the session and
   * routes the eventual upload accordingly. Defaults to item image
   * processing to preserve existing call sites. */
  targetFlow?: MobileCaptureTargetFlow
  /** Required when targetFlow is workspace_asset_library — bound server-side. */
  siteId?: string
  /** Bound at QR creation for audio_asset_library — phone never supplies this. */
  audioPresentationType?: MobileAudioPresentationType
  /** i18n key under autoProcess for destination-specific upload hint copy. */
  uploadHintKey?: string
  /** Called while the modal is open so the caller's list can poll faster. */
  onSessionActiveChange?: (active: boolean) => void
  /** Session identity + status for session-scoped picker completion (never list-diff before paired). */
  onSessionMetaChange?: (meta: {
    sessionId: string
    createdAt: string
    status: MobileCaptureSessionStatus
  } | null) => void
}

function remainingLabel(expiresAt: string | undefined, nowMs: number, expiredText: string): string {
  if (!expiresAt) return ''
  const ms = new Date(expiresAt).getTime() - nowMs
  if (ms <= 0) return expiredText
  const totalSec = Math.ceil(ms / 1000)
  const m = Math.floor(totalSec / 60)
  const s = totalSec % 60
  return `${m}:${String(s).padStart(2, '0')}`
}

export default function MobileCaptureQrModal({
  open,
  workspaceId,
  onClose,
  targetFlow = 'item_image_processing',
  siteId,
  audioPresentationType,
  uploadHintKey = 'mobileCapture.uploadHint',
  onSessionActiveChange,
  onSessionMetaChange,
}: Props) {
  const { t } = useTranslation(['autoProcess', 'actions'])
  const [phase, setPhase] = useState<ModalPhase>('initializing')
  const [session, setSession] = useState<MobileCaptureSessionOut | null>(null)
  const [pairingUrl, setPairingUrl] = useState('')
  const [errorCode, setErrorCode] = useState<ErrorCode | null>(null)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [nowMs, setNowMs] = useState(() => Date.now())
  const sessionIdRef = useRef<string | null>(null)
  const createInFlightRef = useRef(false)

  const status: MobileCaptureSessionStatus | 'loading' =
    phase === 'initializing' && !session ? 'loading' : session?.status || 'pending'

  const createSession = useCallback(async () => {
    if (!workspaceId || createInFlightRef.current) return
    if (targetFlow === 'workspace_asset_library' && !siteId) {
      setSession(null)
      setPairingUrl('')
      setErrorCode('QR_SESSION_CREATE_FAILED')
      setErrorMessage(t('mobileCapture.sessionCreateFailedMessage'))
      setPhase('error')
      return
    }
    createInFlightRef.current = true
    setPhase('initializing')
    setErrorCode(null)
    setErrorMessage(null)
    try {
      const { data } = await createMobileCaptureSession(
        workspaceId,
        targetFlow,
        siteId,
        audioPresentationType,
      )
      sessionIdRef.current = data.session_id
      setSession(data)
      const resolved = resolveSessionPairingUrl(data)
      const mobileReachable = resolved.mobileReachable && !isLoopbackOrigin(resolved.url)
      setPairingUrl(resolved.url)
      setPhase(mobileReachable ? 'ready' : 'local_unreachable')
    } catch (e) {
      // `saasApi`'s response interceptor already unwraps Axios errors into a
      // plain `Error` with a top-level `.status` (undefined for network/
      // timeout failures where no HTTP response was ever received).
      const err = e as { status?: number; message?: string }
      setSession(null)
      setPairingUrl('')
      setErrorCode(err?.status ? 'QR_SESSION_CREATE_FAILED' : 'NETWORK_ERROR')
      setErrorMessage(err?.message || t('mobileCapture.sessionCreateFailedMessage'))
      setPhase('error')
    } finally {
      createInFlightRef.current = false
    }
  }, [audioPresentationType, siteId, targetFlow, t, workspaceId])

  useEffect(() => {
    if (!open) {
      onSessionMetaChange?.(null)
      return
    }
    void createSession()
    onSessionActiveChange?.(true)
    return () => {
      onSessionActiveChange?.(false)
      onSessionMetaChange?.(null)
      const sid = sessionIdRef.current
      sessionIdRef.current = null
      if (sid && workspaceId) {
        void revokeMobileCaptureSession(workspaceId, sid).catch(() => undefined)
      }
      setSession(null)
      setPairingUrl('')
      setErrorCode(null)
      setErrorMessage(null)
      setPhase('initializing')
    }
    // Intentionally only on open/close — do not recreate on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, workspaceId])

  useEffect(() => {
    if (!open) return
    const t = window.setInterval(() => setNowMs(Date.now()), 1000)
    return () => window.clearInterval(t)
  }, [open])

  useEffect(() => {
    if (!open || !session?.session_id) {
      onSessionMetaChange?.(null)
      return
    }
    onSessionMetaChange?.({
      sessionId: session.session_id,
      createdAt: session.created_at || new Date().toISOString(),
      status: session.status,
    })
  }, [open, onSessionMetaChange, session?.created_at, session?.session_id, session?.status])

  useEffect(() => {
    if (!open || phase !== 'ready' || !session?.session_id || !workspaceId) return
    if (session.status === 'revoked' || session.status === 'expired') return
    const t = window.setInterval(() => {
      void getMobileCaptureSession(workspaceId, session.session_id)
        .then(({ data }) => setSession((prev) => ({ ...prev, ...data, pairing_token: prev?.pairing_token })))
        .catch(() => undefined)
    }, 2000)
    return () => window.clearInterval(t)
  }, [open, phase, session?.session_id, session?.status, workspaceId])

  const expired =
    status === 'expired' || (session?.expires_at ? new Date(session.expires_at).getTime() <= nowMs : false)

  const statusHint = useMemo(() => {
    if (phase !== 'ready') return ''
    if (expired) return t('mobileCapture.qrExpired')
    if (status === 'paired') return t('mobileCapture.paired')
    if (status === 'revoked') return t('mobileCapture.revoked')
    return t('mobileCapture.waitingScan')
  }, [phase, status, expired, t])

  const handleClose = () => onClose()
  const handleRegenerate = () => void createSession()

  const showQr = phase === 'ready' && Boolean(pairingUrl) && !expired && status !== 'revoked'
  const showRegenerate = phase === 'error' || phase === 'local_unreachable' || (phase === 'ready' && (expired || status === 'revoked'))

  return (
    <Modal
      open={open}
      title={t('mobileCapture.title')}
      onCancel={handleClose}
      footer={
        <Space>
          {showRegenerate && (
            // Once regenerate is clicked, `phase` flips to 'initializing'
            // and this whole button unmounts (the spinner view takes over)
            // — there is no in-between "loading" state for this button to
            // show.
            <Button icon={<ReloadOutlined />} onClick={handleRegenerate} data-testid="mobile-capture-regenerate">
              {t('mobileCapture.regenerate')}
            </Button>
          )}
          <Button onClick={handleClose}>{t('actions:close')}</Button>
        </Space>
      }
      destroyOnHidden
      width={420}
      zIndex={2100}
      data-testid="mobile-capture-qr-modal"
      data-phase={phase}
    >
      {phase === 'initializing' && (
        <div style={{ textAlign: 'center', padding: '32px 0' }} data-testid="mobile-capture-initializing">
          <Spin size="large" />
          <Paragraph type="secondary" style={{ marginTop: 16, marginBottom: 0 }}>
            {t('mobileCapture.initializing')}
          </Paragraph>
        </div>
      )}

      {phase === 'error' && (
        <Alert
          type="error"
          showIcon
          message={errorCode === 'NETWORK_ERROR' ? t('mobileCapture.networkError') : t('mobileCapture.sessionCreateFailed')}
          description={errorMessage || undefined}
          data-testid="mobile-capture-error"
        />
      )}

      {phase === 'local_unreachable' && (
        <div style={{ textAlign: 'center' }}>
          <Paragraph type="secondary" style={{ marginBottom: 16 }}>
            {t('mobileCapture.scanHint')}
            <br />
            {t(uploadHintKey)}
          </Paragraph>
          <Alert
            type="error"
            showIcon
            style={{ marginBottom: 12, textAlign: 'left' }}
            data-testid="mobile-capture-loopback-warning"
            message={t('mobileCapture.loopbackTitle')}
            description={
              <div>
                <div>{t('mobileCapture.loopbackLine1')}</div>
                <div style={{ marginTop: 8 }}>{t('mobileCapture.loopbackLine2')}</div>
                <ul style={{ margin: '8px 0 0', paddingLeft: 18 }}>
                  <li dangerouslySetInnerHTML={{ __html: t('mobileCapture.loopbackLan') }} />
                  <li dangerouslySetInnerHTML={{ __html: t('mobileCapture.loopbackEnv') }} />
                </ul>
                <div style={{ marginTop: 8 }}>{t('mobileCapture.loopbackSameNetwork')}</div>
              </div>
            }
          />
        </div>
      )}

      {phase === 'ready' && (
        <div style={{ textAlign: 'center' }}>
          <Paragraph type="secondary" style={{ marginBottom: 16 }}>
            {t('mobileCapture.scanHint')}
            <br />
            {t(uploadHintKey)}
          </Paragraph>

          {showQr ? (
            <div
              style={{
                display: 'inline-block',
                padding: 12,
                background: '#fff',
                borderRadius: 8,
                border: '1px solid #e5e5e5',
              }}
              data-testid="mobile-capture-qr"
            >
              <QRCodeSVG value={pairingUrl} size={220} level="M" includeMargin={false} />
            </div>
          ) : (
            <Alert type="warning" showIcon message={statusHint} data-testid="mobile-capture-qr-unavailable" />
          )}

          <div style={{ marginTop: 16 }}>
            <Text strong>{statusHint}</Text>
          </div>
          {!expired && status !== 'revoked' && session?.expires_at && (
            <div style={{ marginTop: 8 }}>
              <Text type="secondary">{t('mobileCapture.remainingTime', { time: remainingLabel(session.expires_at, nowMs, t('mobileCapture.expired')) })}</Text>
            </div>
          )}
          {/* VistoeraTASK-00602 — hide manual pairing URL / copy / LAN hint;
              keep pairingUrl only as QR payload. Footer copy is shared by every
              MobileCaptureQrModal entry (image assets, item/flat processing, pickers). */}
          {!expired && status !== 'revoked' && (
            <Paragraph
              type="secondary"
              style={{ marginTop: 16, marginBottom: 0, fontSize: 12, textAlign: 'center' }}
              data-testid="mobile-capture-close-refresh-hint"
            >
              {t('mobileCapture.closeAfterScanHint')}
            </Paragraph>
          )}
        </div>
      )}
    </Modal>
  )
}
